import { useContext, useEffect, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/** Limite de sécurité par défaut (px) : bien au-delà d'un formulaire, même en colonne étroite. */
export const DEFAULT_MAX_HEIGHT = 5000

/**
 * Page intégrée en iframe : transmet à la page PARENTE la hauteur de son
 * contenu, à chaque changement — `{type: <messageType>, height: <px>}` —, pour
 * qu'elle ajuste la hauteur du cadre (plus d'ascenseur interne).
 *
 * Pourquoi un composant (échappatoire config-first) : reactive-json n'a aucun
 * déclencheur sur un changement de TAILLE (événements DOM, message, hashchange,
 * minuterie seulement) ni aucun moyen de lire une hauteur rendue dans le store :
 * la réaction `postMessage` existe, mais n'aurait rien à transporter.
 *
 * CE QUI EST MESURÉ — le bas du canevas des blocs (`#builder-canvas`), pas
 * `document.documentElement.scrollHeight` : encadrée, la page a la hauteur de
 * l'iframe, et `scrollHeight` vaut AU MOINS cette hauteur. Le cadre pourrait
 * grandir mais jamais rétrécir (confirmation plus courte que le formulaire
 * qu'elle remplace). Le canevas, lui, ne dépend pas de la hauteur du cadre —
 * tant qu'aucun bloc de la page n'est dimensionné en `vh` : un bloc de 100vh ou
 * plus ferait grandir le cadre sans fin, un bloc de 60vh le gonflerait
 * (environ 2,5 fois son contenu).
 *
 * LIMITE DE SÉCURITÉ — `maxHeight` (5000 px par défaut) : la hauteur envoyée
 * n'y dépasse jamais, et la boucle ci-dessus s'arrête là au lieu de courir
 * sans fin. Au-delà, le contenu défile DANS le cadre (avertissement en console).
 *
 * QUAND — dès le montage, au `load` et une fois les polices prêtes, par une
 * mesure DIRECTE : hors écran, le navigateur suspend le rendu d'une iframe
 * d'une autre origine (ni `requestAnimationFrame` ni `ResizeObserver`), et un
 * formulaire posé bas dans la page parente n'aurait envoyé sa hauteur qu'une
 * fois atteint, en faisant sauter la page. Ensuite : `ResizeObserver` (rendu
 * actif) et `MutationObserver` (rendu suspendu), regroupés à l'image, avec un
 * délai pour relais quand l'image ne vient pas.
 *
 * Rien n'est envoyé hors iframe, ni dans le générateur de pages statiques
 * (jsdom n'est pas encadré), ni deux fois la même hauteur.
 *
 * YAML :
 *   - type: ParentFrameHeight
 *     messageType: iframe-height        # défaut
 *     targetOrigin: https://www.exemple.fr   # défaut "*" : une hauteur n'a rien de sensible
 *     maxHeight: 5000                   # défaut ; vide ou invalide = défaut
 *
 * Enregistré dans [builderPlugins.js](../builderPlugins.js) sous `element`.
 */
export const ParentFrameHeight = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (value) => evaluateTemplateValue({ valueToEvaluate: value, globalDataContext, templateContext })

  const messageType = resolve(props?.messageType) || 'iframe-height'
  const targetOrigin = resolve(props?.targetOrigin) || '*'
  const maxHeight = Number(resolve(props?.maxHeight)) > 0 ? Number(resolve(props?.maxHeight)) : DEFAULT_MAX_HEIGHT
  const markerRef = useRef(null)

  useEffect(() => {
    const marker = markerRef.current
    if (!marker || !isFramed() || typeof ResizeObserver === 'undefined') return undefined
    const target = marker.closest('#builder-canvas') || document.body

    let active = true
    let last = null
    let frame = 0
    let timer = 0
    let pending = false
    let warned = false
    const send = () => {
      pending = false
      cancelAnimationFrame(frame)
      clearTimeout(timer)
      if (!active) return
      const measured = bottomOf(target)
      const height = Math.min(measured, maxHeight)
      if (measured > maxHeight && !warned) {
        warned = true
        console.warn(`ParentFrameHeight : contenu de ${measured} px, hauteur plafonnée à ${maxHeight} px (bloc dimensionné sur l'écran ?)`)
      }
      if (height === last) return
      last = height
      window.parent.postMessage({ type: messageType, height }, targetOrigin)
    }
    // Une rafale (images, polices, animation) : un envoi par image — ou par délai
    // quand le rendu de l'iframe est suspendu et que l'image ne vient pas.
    const schedule = () => {
      if (pending) return
      pending = true
      frame = requestAnimationFrame(send)
      timer = setTimeout(send, 250)
    }
    const resizeObserver = new ResizeObserver(schedule)
    resizeObserver.observe(target)
    const mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(target, { childList: true, subtree: true, attributes: true, characterData: true })
    window.addEventListener('load', send)
    document.fonts?.ready.then(send)
    send()
    return () => {
      active = false
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('load', send)
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [messageType, targetOrigin, maxHeight])

  return (
    <ActionDependant {...props}>
      <span ref={markerRef} hidden />
    </ActionDependant>
  )
}

/** Encadrée ? Un `top` d'une autre origine est illisible : c'est qu'on l'est. */
const isFramed = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/** Le bas de l'élément dans le document, au pixel supérieur. */
export const bottomOf = (element) => Math.ceil(element.getBoundingClientRect().bottom + window.scrollY)
